import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { getDbOrThrow } from '@/lib/firebase'

/**
 * Units of measurement (`units`) — a shared catalog: readable by any signed-in user, writable by
 * admins only (enforced by the rules). The field set mirrors `FirestoreUnitRepository`, including
 * the author stamps, so documents written here are indistinguishable from the app's own.
 *
 * Reading is already covered by `subscribeToUnits` in `materials-api.ts`; the material editor needs
 * the same list.
 */
export type UnitDraft = {
  name: string
  nameI18n: Record<string, string>
  symbol: string
  isActive: boolean
}

export function createUnit(
  draft: UnitDraft,
  actor: { uid: string; label: string },
): Promise<unknown> {
  return addDoc(collection(getDbOrThrow(), 'units'), {
    name: draft.name,
    nameI18n: draft.nameI18n,
    symbol: draft.symbol,
    isActive: draft.isActive,
    createdBy: actor.uid,
    createdByName: actor.label,
    updatedBy: actor.uid,
    updatedByName: actor.label,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

/** Update touches only the editable fields plus the "updated by" stamps, like the app does. */
export function updateUnit(
  id: string,
  draft: UnitDraft,
  actor: { uid: string; label: string },
): Promise<void> {
  return updateDoc(doc(getDbOrThrow(), 'units', id), {
    name: draft.name,
    nameI18n: draft.nameI18n,
    symbol: draft.symbol,
    isActive: draft.isActive,
    updatedBy: actor.uid,
    updatedByName: actor.label,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Deletes a unit. Materials keep their `unit` symbol as plain text, so an estimate that already uses
 * the unit keeps rendering; only the reference (`unitId`) goes stale. Prefer deactivating over
 * deleting — that is also what the app's units screen suggests.
 */
export function deleteUnit(id: string): Promise<void> {
  return deleteDoc(doc(getDbOrThrow(), 'units', id))
}
